import type { FlowType } from '@core/enums/flow-type.enum';
import type { EIdentityDocumentType } from '@core/enums/identity-document-type.enum';
import type { PageKey } from '@core/enums/page-keys.enum';
import type { RecordingType } from '@core/enums/recording-type.enum';
import type { TraineeType } from '@core/enums/trainee-type.enum';
import type { VisitorType } from '@core/enums/visitor-type.enum';
import type { ExportControl } from '@core/model/export-control/export-control.model';
import type { CheckinProcessConfiguration } from '@core/model/training-center/checkin-process-configuration.model';
import type { TSAStatus } from '@core/enums/tsa-status.enum';
import type { VisitStatus } from '@core/enums/visit-status.enum';

export enum FlowId {
  CheckinProcess = 'checkin-process',
  ScanPassport = 'ScanPassportProcess',
  ScanGovernmentId = 'ScanGovernmentIdProcess',
  ConsentIdentityVerification = 'ConsentIdentityVerificationProcess',
  TraineeTypeSelection = 'TraineeTypeSelectionProcess',
  CovidAcknowledgement = 'CovidAcknowledgementProcess',
  ReportToReception = 'ReportToReceptionProcess',
  PhotoId = 'PhotoIdProcess',
  PhotoSelfie = 'PhotoSelfieProcess',
  IdentityComparison = 'IdentityComparisonProcess',
  Checkin = 'CheckinProcess',
  PrintBadge = 'PrintBadgeProcess',
  DailyCheckinCheckout = 'DailyCheckinCheckoutProcess',
  QrCodeValidation = 'QrCodeValidationProcess',
  GreetingsWelcome = 'GreetingsWelcomeProcess',
  FacialRecognition = 'FacialRecognitionProcess',
  ExportControlValidation = 'ExportControlValidationProcess',
  Manual = 'ManualFlow',
  CovidDeclaration = 'CovidDeclarationFlow',
  Reprint = 'ReprintFlow',
  EmailConsent = 'EmailConsentProcess',
  IdentityPolicyFail = 'IdentityPolicyFailProcess',
  CustomerSelection = 'CustomerSelectionProcess',
  AircraftSelection = 'AircraftSelectionProcess',
  ReservationSelection = 'ReservationSelectionProcess',
  IdentityDocumentType = 'IdentityDocumentTypeProcess',
  NoIdentityDocument = 'NoIdentityDocumentProcess',
  ExistingQrCode = 'ExistingQRCode',
  PreCheckin = 'PreCheckinFlow',
  TrainingSelection = 'TrainingSelectionProcess',
  ContactInformation = 'ContactInformationProcess',
  TrainingSummary = 'TrainingSummaryProcess',
  DailyCheckinCheckoutFlow = 'DailyCheckinCheckoutFlow',
  CheckinType = 'CheckinTypeProcess',
  CheckinSummary = 'CheckinSummaryProcess',
  ScanDocument = 'ScanDocumentProcess',
  IdentityComparisonFail = 'IdentityComparisonFailProcess',
  CreateAccount = 'CreateAccountProcess',
}

export interface WorkflowVariables {
  readonly flowType?: FlowType;
  readonly visitorType?: VisitorType;
  readonly traineeType?: TraineeType;
  readonly recordingType?: RecordingType;
  readonly identityDocumentType?: EIdentityDocumentType;
  readonly checkinProcessConfiguration?: CheckinProcessConfiguration;
  readonly hasConsentedIdentityVerification: boolean;
  readonly hasAcknowledgeCovidVerification: boolean;
  readonly hasExtraInformationPage: boolean;
  readonly isScanPassportSuccess: boolean;
  readonly isScanGovernmentIdSuccess: boolean;
  readonly isScanDocumentSuccess: boolean;
  readonly isIdentityComparaisonSuccess: boolean;
  readonly isCheckinSuccess: boolean;
  readonly isIdPictureSuccess: boolean;
  readonly isSelfiePictureSuccess: boolean;
  readonly isPrintBadgeSuccess: boolean;
  readonly isScanQrCodeValidationSuccess: boolean;
  readonly trainingId?: string;
  readonly visitStatus?: VisitStatus;
  readonly tsaStatus?: TSAStatus;
  readonly isVisitPrecheckedIn: boolean;
  readonly isEmailProvided: boolean;
  readonly skipPrintBadge: boolean;
  readonly exportControl?: ExportControl;
}

export type StepResult<TStep extends string> =
  | { nextStep: TStep }
  | { flow: CheckinWorkflow<string>; returnStep: TStep }
  | { complete: true }
  | { bubble: 'back' | 'exit' };

export interface CheckinWorkflowStep<TStep extends string> {
  id: TStep;
  page?: PageKey;
  next(variables: WorkflowVariables): StepResult<TStep>;
  back?(variables: WorkflowVariables): StepResult<TStep>;
  bypass?(variables: WorkflowVariables): StepResult<TStep>;
  exit?(variables: WorkflowVariables): StepResult<TStep>;
}

export interface CheckinWorkflow<TStep extends string> {
  id: FlowId;
  entry: TStep;
  steps: Record<TStep, CheckinWorkflowStep<TStep>>;
}
